import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { WhatsAppIcon, WA_LINK } from "@/components/marketing/WhatsAppIcon";

const LINKS = [
  { label: "About", href: "#about", id: "about" },
  { label: "Named Patient", href: "#named-patient", id: "named-patient" },
  { label: "LATAM", href: "#latam", id: "latam" },
  { label: "MENA", href: "#mena", id: "mena" },
  { label: "Africa", href: "#africa", id: "africa" },
  { label: "Services", href: "#services", id: "services" },
  { label: "Why Heliopolis", href: "#why-us", id: "why-us" },
  { label: "Global Network", href: "#network", id: "network" },
  { label: "Contact", href: "#contact", id: "contact" },
];

export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-[#D7E3F2] shadow-[0_6px_24px_-16px_rgba(13,27,42,0.4)]" : "bg-transparent"}`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-[72px]">
        <a href="#top" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <img src="/heliopolis-logo.png" alt="Heliopolis Pharmaceuticals" className="h-10 sm:h-12 w-auto" />
        </a>

        <div className="hidden lg:flex items-center gap-4 xl:gap-7">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              className="relative text-[13px] xl:text-sm font-medium text-[#0D1B2A]/80 hover:text-[#245FAF] transition-colors after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-[#F5C64D] after:transition-all hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-button"
            aria-label="Chat on WhatsApp"
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#25D366]/40 bg-[#25D366]/10 px-4 py-2.5 text-sm font-semibold text-[#128C7E] transition-all hover:bg-[#25D366] hover:text-white"
          >
            <WhatsAppIcon size={17} /> <span className="hidden xl:inline">WhatsApp</span>
          </a>
          <Link
            to="/login"
            data-testid="nav-portal-button"
            className="group relative hidden sm:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#377DD3] to-[#245FAF] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_10px_28px_-10px_rgba(55,125,211,0.9)] ring-1 ring-inset ring-[#F5C64D]/50 transition-all hover:scale-[1.03] hover:shadow-[0_14px_34px_-10px_rgba(55,125,211,1)]"
          >
            <span className="absolute inset-0 rounded-full bg-[#F5C64D]/25 blur-md opacity-0 group-hover:opacity-100 transition-opacity -z-10" />
            Enter Portal
            <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
          <button
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full text-[#0D1B2A] hover:bg-[#EAF2FB]"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden border-t border-[#D7E3F2] bg-white/95 backdrop-blur-xl px-4 py-4"
          data-testid="nav-mobile-menu"
        >
          <div className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="rounded-xl px-4 py-3 text-base font-medium text-[#0D1B2A] hover:bg-[#EAF2FB]"
              >
                {l.label}
              </a>
            ))}
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-whatsapp-button"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-base font-semibold text-white"
            >
              <WhatsAppIcon size={18} /> Chat on WhatsApp
            </a>
            <Link
              to="/login"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#377DD3] to-[#245FAF] px-5 py-3 text-base font-semibold text-white ring-1 ring-inset ring-[#F5C64D]/50"
              data-testid="nav-mobile-portal-button"
            >
              Enter Portal <ArrowRight size={16} />
            </Link>
          </div>
        </motion.div>
      )}
    </header>
  );
}
